<template>
    <div class="w-full">
        <Message severity="info" :closable="false" class="mb-4 w-full">
            Beneficiário é quem <b>recebe</b> o boleto — pode ser o próprio provedor ou um
            gateway/cobrador. É este nome/CNPJ que vai no Segmento J-52 da remessa CNAB desta conta.
        </Message>

        <!-- Gateways já vistos/cadastrados no provedor deste contrato: evita digitar CNPJ de cabeça. -->
        <div v-if="provedorId" class="field mt-5">
            <FloatLabel>
                <Select
                    id="benef_conhecido"
                    v-model="conhecidoSelecionado"
                    :options="conhecidos"
                    optionLabel="label"
                    :loading="isLoading"
                    :disabled="readOnly"
                    showClear
                    class="w-full"
                    @change="aplicarConhecido" />
                <label for="benef_conhecido">Gateway conhecido{{ provedorNome ? ` — provedor ${provedorNome}` : '' }}</label>
            </FloatLabel>
            <small v-if="!isLoading && !conhecidos.length" class="text-gray-500 block mt-1">
                Nenhum gateway conhecido para este provedor ainda — preencha manualmente abaixo.
            </small>
        </div>

        <div class="text-sm text-gray-500 my-4">— ou preencha manualmente —</div>

        <div class="field mt-5">
            <FloatLabel>
                <InputText id="beneficiario_nome" v-model="data.beneficiario_nome" class="w-full" :disabled="readOnly" />
                <label for="beneficiario_nome">Beneficiário (nome)</label>
            </FloatLabel>
        </div>

        <div class="field mt-5">
            <FloatLabel>
                <InputText id="beneficiario_cnpj" v-model="data.beneficiario_cnpj" class="w-full" :disabled="readOnly" />
                <label for="beneficiario_cnpj">CNPJ do beneficiário</label>
            </FloatLabel>
            <small v-if="cnpjInvalido" class="text-red-500 block mt-1">
                Informe um CNPJ com 14 dígitos ou deixe em branco.
            </small>
        </div>

        <div class="field mt-4 flex align-items-start gap-2">
            <Checkbox
                inputId="beneficiario_pre_cadastrar"
                v-model="data.beneficiario_pre_cadastrar"
                :binary="true"
                :disabled="readOnly || !podePreCadastrar" />
            <label for="beneficiario_pre_cadastrar" class="text-sm">
                Cadastrar também no provedor — o nome passa a valer na remessa deste provedor
                e silencia o aviso de divergência nas próximas contas.
            </label>
        </div>
        <small v-if="!podePreCadastrar" class="text-gray-500 block">
            Requer nome, CNPJ com 14 dígitos e contrato vinculado.
        </small>

        <Message severity="secondary" :closable="false" class="mt-4 w-full">
            As alterações desta aba são gravadas no <b>Salvar</b> da conta.
        </Message>
    </div>
</template>

<script>
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import FloatLabel from 'primevue/floatlabel';
import Message from 'primevue/message';

import Cotacao from '@/service/cotacao.js';
import Provedor from '@/service/provedor.js';
import Mask from '@/utils/Mask.js';

export default {
    components: { InputText, Select, Checkbox, FloatLabel, Message },
    props: {
        /** Objeto `form` da conta — os campos beneficiario_* são editados direto nele. */
        data: {},
        readOnly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            conhecidos: [],
            conhecidoSelecionado: null,
            provedorId: null,
            provedorNome: '',
            isLoading: false,
        };
    },
    computed: {
        cnpjDigitos() {
            return (this.data?.beneficiario_cnpj || '').replace(/\D/g, '');
        },
        cnpjInvalido() {
            return this.cnpjDigitos.length > 0 && this.cnpjDigitos.length !== 14;
        },
        podePreCadastrar() {
            return !!(this.data?.contrato_id)
                && !!(this.data?.beneficiario_nome || '').trim()
                && this.cnpjDigitos.length === 14;
        },
    },
    watch: {
        podePreCadastrar(pode) {
            // Desmarca sozinho se o usuário apagar nome/CNPJ depois de marcar.
            if (!pode && this.data) {
                this.data.beneficiario_pre_cadastrar = false;
            }
        },
    },
    mounted() {
        // CNPJ vem do banco só com dígitos — mascara pra leitura (backend re-normaliza no saving).
        if (this.data && this.cnpjDigitos.length === 14) {
            this.data.beneficiario_cnpj = Mask.cnpj(this.cnpjDigitos);
        }
        this.resolverProvedor().then(() => this.loadConhecidos());
    },
    methods: {
        /**
         * Provedor do contrato desta conta — o combo de gateways é por provedor.
         * Resolve pela cotação aprovada, o mesmo caminho que o backend usa
         * (contrato → cotação aprovada → provedor).
         *
         * ponytail: usa `listContrato`, que já devolve `cot.*` (logo `provedor_id`)
         * e o nome do provedor — sem campo novo na listagem de contas nem join
         * novo na de contratos. Custa uma requisição ao abrir a aba. Não filtra
         * por `aprovado` porque `cot.aprovado` é like_field e viraria ILIKE sobre
         * smallint (erro no Postgres); a página de 15 cobre o caso real, onde o
         * contrato com mais cotações tem 2.
         */
        resolverProvedor() {
            const contratoId = this.data?.contrato_id;
            if (!contratoId) {
                return Promise.resolve();
            }

            this.isLoading = true;
            return Cotacao.listContrato(contratoId)
                .then(response => {
                    const aprovada = (response.data?.data || [])
                        .find(c => Number(c.aprovado) === 1);

                    this.provedorId = aprovada?.provedor_id ?? null;
                    this.provedorNome = aprovada?.provedor || '';
                })
                .catch(() => {
                    this.provedorId = null;
                    this.provedorNome = '';
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        /**
         * Lista os gateways do provedor deste contrato.
         * ponytail: reusa o endpoint do mapa Gateway ↔ Provedor (busca por nome do provedor)
         * e filtra a página pelo provedor_id exato — sem endpoint/permissão nova. Só enxerga
         * gateways que já apareceram em alguma conta; se um dia precisar listar pré-cadastros
         * "virgens", aí vale um GET próprio em `beneficiarios`.
         */
        loadConhecidos() {
            const provedorId = this.provedorId;
            if (!provedorId) {
                this.conhecidos = [];
                return;
            }

            const params = this.provedorNome ? `q=${encodeURIComponent(this.provedorNome)}` : '';

            this.isLoading = true;
            Provedor.beneficiarioMapa(params)
                .then(response => {
                    const provedor = (response.data?.data || [])
                        .find(p => Number(p.id) === Number(provedorId));

                    this.conhecidos = (provedor?.gateways || []).map(g => {
                        const nome = g.nome_cadastrado || g.beneficiario_nome || '(sem nome)';
                        return {
                            label: `${nome} — ${Mask.cnpj(g.beneficiario_cnpj)}${g.is_proprio ? ' · próprio provedor' : ''}`,
                            nome: g.nome_cadastrado || g.beneficiario_nome || '',
                            cnpj: g.beneficiario_cnpj,
                        };
                    });
                })
                .catch(() => {
                    this.conhecidos = [];
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        aplicarConhecido() {
            if (!this.conhecidoSelecionado) {
                return;
            }
            this.data.beneficiario_nome = this.conhecidoSelecionado.nome;
            this.data.beneficiario_cnpj = Mask.cnpj(this.conhecidoSelecionado.cnpj);
        },
    },
};
</script>
